import React, { useId } from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { Label } from "@radix-ui/react-label";
import styles from "./styles.module.scss";
import clsx from "clsx";

type SelectableItem = { value: string; label?: string; items?: Item[] };
type GroupLabelItem = {
  value?: string;
  label: string;
  items: Item[];
  isGroupLabel: true;
};
export type Item = SelectableItem | GroupLabelItem;

const isGroupLabelItem = (item: Item): item is GroupLabelItem =>
  "isGroupLabel" in item && item.isGroupLabel;

interface SelectProps {
  id?: string;
  label: string;
  value: string;
  format?: "control-bar" | "auto-width";
  onChange(value: string): void;
  items: Item[];
}

function renderItems(items: Item[]): React.ReactNode {
  return items.map((item) => {
    if (isGroupLabelItem(item)) {
      return (
        <RadixSelect.Group key={item.label}>
          <RadixSelect.Label className={styles["group-label"]}>
            {item.label}
          </RadixSelect.Label>
          <div className={styles.group}>
            <div className={styles["sub-group"]}>{renderItems(item.items)}</div>
          </div>
        </RadixSelect.Group>
      );
    }

    const itemText = item.label || item.value;

    const renderedItem = (
      <RadixSelect.Item value={item.value} className={styles.item}>
        <RadixSelect.ItemText>{itemText}</RadixSelect.ItemText>
        <RadixSelect.ItemIndicator className={styles.indicator}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z"
              fill="currentColor"
              fillRule="evenodd"
              clipRule="evenodd"
            />
          </svg>
        </RadixSelect.ItemIndicator>
      </RadixSelect.Item>
    );
    return item.items?.length ? (
      <RadixSelect.Group key={item.value}>
        <RadixSelect.Label className={styles.hide}>
          {itemText}
        </RadixSelect.Label>
        <div className={styles.group}>
          <div className={styles.parent}>{renderedItem}</div>
          <div className={styles["sub-group"]}>{renderItems(item.items)}</div>
        </div>
      </RadixSelect.Group>
    ) : (
      <div className={styles.single} key={item.value}>
        {renderedItem}
      </div>
    );
  });
}

const findItem = (items: Item[], value: string): SelectableItem | undefined => {
  for (const item of items) {
    if (!isGroupLabelItem(item) && item.value === value) {
      return item;
    }
    if (item.items) {
      const found = findItem(item.items, value);
      if (found) {
        return found;
      }
    }
  }
};

export function Select({
  id: propsId,
  label,
  value,
  format,
  onChange,
  items,
}: SelectProps) {
  const randomId = useId();
  const id = propsId || randomId;

  const selectedItem = findItem(items, value);
  const itemText = selectedItem?.label || selectedItem?.value;

  return (
    <div
      data-component="select"
      className={clsx(styles.container, {
        [styles["control-bar"]]: format === "control-bar",
        [styles["auto-width"]]: format == "auto-width",
      })}
    >
      <Label htmlFor={id} className={styles.label}>
        {label}
      </Label>
      <RadixSelect.Root value={value} onValueChange={onChange}>
        <RadixSelect.Trigger id={id} className={styles.button}>
          <RadixSelect.Value>{itemText}</RadixSelect.Value>
        </RadixSelect.Trigger>
        <RadixSelect.Portal className={styles.portal}>
          <RadixSelect.Content className={styles.content}>
            <RadixSelect.ScrollUpButton>
              <div className={styles["scroll-button-up"]} />
            </RadixSelect.ScrollUpButton>
            <RadixSelect.Viewport className={styles.viewport}>
              {renderItems(items)}
            </RadixSelect.Viewport>
            <RadixSelect.ScrollDownButton>
              <div className={styles["scroll-button-down"]} />
            </RadixSelect.ScrollDownButton>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    </div>
  );
}
